<template>
  <nav
    v-if="totalPages > 1"
    class="mt-8 min-w-0 lg:mt-12"
    :aria-label="ariaLabel"
  >
    <!-- Mobile: prev/next + status only (no overflowing page numbers) -->
    <div class="flex min-w-0 items-center justify-between gap-3 lg:hidden">
      <button
        type="button"
        :disabled="currentPage <= 1"
        :class="navButtonClass(currentPage <= 1, true)"
        @click="goToPage(currentPage - 1)"
      >
        <svg class="size-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
        <span class="sr-only">{{ t('pagination.previous') }}</span>
      </button>

      <p class="min-w-0 text-center text-sm font-medium text-neutral-600">
        {{ t('pagination.status', { current: currentPage, total: totalPages }) }}
      </p>

      <button
        type="button"
        :disabled="currentPage >= totalPages"
        :class="navButtonClass(currentPage >= totalPages, true)"
        @click="goToPage(currentPage + 1)"
      >
        <svg class="size-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
        <span class="sr-only">{{ t('pagination.next') }}</span>
      </button>
    </div>

    <!-- Desktop -->
    <div class="hidden items-center justify-between lg:flex">
      <button
        type="button"
        :disabled="currentPage <= 1"
        :class="navButtonClass(currentPage <= 1)"
        @click="goToPage(currentPage - 1)"
      >
        <svg class="size-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
        <span>{{ t('pagination.previous') }}</span>
      </button>

      <div class="flex flex-wrap items-center justify-center gap-3">
        <button
          v-for="page in totalPages"
          :key="`desktop-${page}`"
          type="button"
          :class="pageButtonClass(page)"
          :aria-current="page === currentPage ? 'page' : undefined"
          @click="goToPage(page)"
        >
          {{ formatPageLabel(page) }}
        </button>
      </div>

      <button
        type="button"
        :disabled="currentPage >= totalPages"
        :class="navButtonClass(currentPage >= totalPages)"
        @click="goToPage(currentPage + 1)"
      >
        <span>{{ t('pagination.next') }}</span>
        <svg class="size-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  </nav>
</template>

<script setup>
import { watch } from 'vue';
import { useTranslations } from '@/utils/translations.js';

const { t } = useTranslations();

const props = defineProps({
  currentPage: {
    type: Number,
    required: true,
  },
  totalPages: {
    type: Number,
    required: true,
  },
  ariaLabel: {
    type: String,
    default: 'Paginacja',
  },
});

const emit = defineEmits(['update:currentPage']);

watch(() => props.currentPage, (page, previousPage) => {
  if (previousPage === undefined) return;
  if (page === previousPage) return;
  if (typeof window === 'undefined') return;
  window.scrollTo({ top: 0, behavior: 'smooth' });
});

function goToPage(page) {
  if (page < 1 || page > props.totalPages || page === props.currentPage) return;
  emit('update:currentPage', page);
}

function formatPageLabel(page) {
  return String(page).padStart(2, '0');
}

function navButtonClass(disabled, compact = false) {
  return [
    'flex items-center justify-center gap-1 rounded border text-sm transition-colors',
    compact ? 'size-10 shrink-0 p-0' : 'px-3 py-2',
    disabled
      ? 'cursor-not-allowed border-neutral-100 bg-white text-neutral-300'
      : 'border-neutral-100 bg-white text-neutral-700 hover:border-neutral-200',
  ];
}

function pageButtonClass(page) {
  return [
    'flex h-9 min-w-9 shrink-0 items-center justify-center rounded border px-2 text-sm',
    page === props.currentPage
      ? 'border-neutral-200 bg-white font-medium text-neutral-700'
      : 'border-neutral-100 bg-white text-neutral-700 hover:border-neutral-200',
  ];
}
</script>
